import React from 'react';
import {
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';

export type ConfirmCloseProductModalProps = {
  title: string;
  bodyText?: string;
  confirmLabel: string;
  cancelLabel: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Purely presentational confirmation dialog for the product-close flows. It holds no business
 * logic and dispatches nothing — the owning component (ConfigureProductModal for active e-banking,
 * CloseProductSection for AI-like / non-active products) stays mounted, layers this on top, and
 * injects the title/body/labels plus the confirm and cancel handlers.
 * The confirm button keeps the shared data-testid="pb-close-product-button".
 */
export const ConfirmCloseProductModal: React.FC<ConfirmCloseProductModalProps> = ({
  title,
  bodyText,
  confirmLabel,
  cancelLabel,
  isPending,
  onConfirm,
  onCancel,
}) => {
  const handleCancel = () => {
    if (isPending) return;
    onCancel();
  };

  return (
    <ZfDialog show={true} size="small" disableLightDismiss={isPending} onDialogClose={handleCancel}>
      <ZfDialogHeader showCloseButton={!isPending}>{title}</ZfDialogHeader>
      <ZfDialogContent>
        <ZfTypography variant="body-medium" color="normal" style={{ whiteSpace: 'pre-line' }}>
          {bodyText}
        </ZfTypography>
      </ZfDialogContent>
      <ZfDialogFooter>
        <ZfStack slot="primary" direction="row" spacing="small">
          <ZfTextButton hierarchy="tertiary" size="small" onClick={handleCancel} disabled={isPending}>
            {cancelLabel}
          </ZfTextButton>
          <ZfTextButton
            size="small"
            selected={true}
            hierarchy="primary"
            kind="destructive"
            data-testid="pb-close-product-button"
            id="pb-close-product-button"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending && <ZfLoadingSpinner size="extra-small" />}
            {confirmLabel}
          </ZfTextButton>
        </ZfStack>
      </ZfDialogFooter>
    </ZfDialog>
  );
};
